// Ingestion-lane status resolves against the most recent run in this window, not literally
// "today" — an org that hasn't run yet today must not read as failed.
export const INGESTION_STATUS_LOOKBACK_DAYS = 7

export enum StatusColor {
  Information = "information",
  Success = "success",
  Neutral = "neutral",
  Warning = "warning",
  Error = "error",
}

export enum IngestionStatusTab {
  Ingestion = "App Ingestion",
  DataSteps = "BQ Ingestion",
  DataHealth = "Data Health",
  Events = "Events",
  BigQueryViews = "BQ Views",
  DatasetSource = "Dataset Source",
}

export enum OrgAppIngestionTabSection {
  IngestionLog = "ingestion-log",
  Errors = "errors",
  ForecastIngestionLog = "forecast-ingestion-log",
  RuleViolations = "rule-violations",
  ScheduledUpdates = "scheduled-updates",
  DsPipelineStatus = "ds-pipeline-status",
  RunHistory = "run-history",
}

// Radix hands accordion values back as plain strings; this is the one place that widening
// is narrowed again.
export function toOrgAppIngestionTabSection(value: string): OrgAppIngestionTabSection | null {
  const sections: string[] = Object.values(OrgAppIngestionTabSection)
  return sections.includes(value) ? (value as OrgAppIngestionTabSection) : null
}

// LoadFromGCP.status; absent = rows from before the column existed.
export const RUN_STATUS_META: Record<string, { color: StatusColor; description: string }> = {
  running: { color: StatusColor.Information, description: "Run is in progress" },
  success: {
    color: StatusColor.Success,
    description: "Every table the run covered completed",
  },
  stopped: {
    color: StatusColor.Warning,
    description:
      "The update was stopped mid-run (stopUpdate, or load-from-buckets was disabled). Tables it never reached are still scheduled.",
  },
  partial: {
    color: StatusColor.Warning,
    description:
      "Every table that could load did, but some BigQuery source table or column does not exist for this company yet. The run row's error names what was missing.",
  },
  failed: {
    color: StatusColor.Error,
    description:
      "A table failed. Nothing is retried automatically. The work is picked up by whatever next schedules or triggers it.",
  },
  crashed: {
    color: StatusColor.Error,
    description:
      "Run exceeded 1h without finishing (Cloud Run timeout/OOM). Tables it never reached are still scheduled, and its triggers stay live for the next invocation.",
  },
}

// Where a scheduled table update came from. Written to ScheduledTableUpdate.source by every
// producer, then rolled up into LoadFromGCP.trigger by update_db/update_decision.py.
export type ScheduleSource =
  | "staff"
  | "app:allocation"
  | "app:collection"
  | "app:company_settings"
  | "app:demo"
  | "app:forecast_rule"
  | "app:pre_orders"
  | "app:replacement_link"
  | "app:sku_metadata"

type TriggerVariant = "blue" | "violet" | "mint" | "green" | "yellow" | "neutral"

// LoadFromGCP.trigger is a comma-separated list of origin tokens. The pipeline authors
// pipeline/scheduled/manual/forced/reupload and the `ingestion:`/`forecast:` prefixes
// (update_db/update_decision.py); an unknown token renders as its raw string.
export const TRIGGER_SOURCE_META: Record<
  string,
  { label: string; description: string; variant: TriggerVariant }
> = {
  staff: {
    label: "Staff",
    description: "A Hakio staff member scheduled the update from /internal/status",
    variant: "blue",
  },
  "app:allocation": {
    label: "Allocation planning",
    description: "Allocation ROS recalculation requested from allocation planning",
    variant: "violet",
  },
  "app:collection": {
    label: "Collection",
    description: "A collection was edited in the app",
    variant: "violet",
  },
  "app:company_settings": {
    label: "Company settings",
    description: "A company setting that changes ingested data was changed",
    variant: "violet",
  },
  "app:demo": {
    label: "Demo mapping",
    description: "Demo category mappings were changed",
    variant: "violet",
  },
  "app:forecast_rule": {
    label: "Forecast rule",
    description: "A max-forecast-change rule was created, edited or deleted",
    variant: "violet",
  },
  "app:pre_orders": {
    // Staff-only page — Hakio's own vocabulary is correct here, not the tenant's naming
    /* eslint-disable-next-line @hakio/no-untranslated-terminology */
    label: "Pre-orders",
    /* eslint-disable-next-line @hakio/no-untranslated-terminology */
    description: "The only-pre-orders flag was recalculated",
    variant: "violet",
  },
  "app:replacement_link": {
    label: "Replacement link",
    description: "Replacement links were created or deleted",
    variant: "violet",
  },
  "app:sku_metadata": {
    label: "SKU metadata",
    description: "SKU metadata was edited in the app",
    variant: "violet",
  },
  ingestion: {
    label: "Ingestion",
    description: "An ingestion event landed new views in BigQuery",
    variant: "mint",
  },
  forecast: {
    label: "Forecast",
    description: "A DS forecast table in BigQuery was modified since the last run",
    variant: "green",
  },
  pipeline: {
    label: "Pipeline",
    description: "The pipeline itself scheduled a full resync (e.g. after a company reupload)",
    variant: "neutral",
  },
  scheduled: {
    label: "Scheduled",
    description: "Queued before the source column existed, so the origin is unknown",
    variant: "neutral",
  },
  manual: {
    label: "Manual",
    description: "load-from-buckets was run directly (CLI / local)",
    variant: "neutral",
  },
  forced: {
    label: "Forced",
    description: "Run started with --force: every table, regardless of triggers",
    variant: "yellow",
  },
  reupload: {
    label: "Reupload",
    description: "Schema was dropped and reloaded from prod data",
    variant: "yellow",
  },
}
